import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { DefaultKpi, IKpi } from 'src/Definitions';
import { Client, IKpiDto, KpiDto, SwaggerResponse } from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';
import DataTableStore from '../DataTableStore';

const ColumnSettingsDto = 'KpiDto';

export default class KpisStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public isModalOpen: boolean;
    @observable
    public kpis: IKpi[];
    @observable
    public selectedKpi?: IKpi;
    public dataTableStore: DataTableStore<IKpiDto, IKpi>;

    constructor(private globalStore: GlobalStore, private client: Client) {
        this.dataTableStore = new DataTableStore<IKpiDto, IKpi>(
            globalStore,
            ({ filter, page, orderBy, includeInactives }) =>
                client.getAllKpis(filter, page, undefined, orderBy, includeInactives),
            (kpi) => this.setupKpis(kpi),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );
    }

    @action.bound
    public closeDialog() {
        this.isModalOpen = false;
        this.selectedKpi = undefined;
    }

    @action.bound
    public disableKpi(kpi: IKpi) {
        this.isLoading = true;
        return this.client
            .archiveKpi(kpi.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getColumnSettingsAndKpis() {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;
        return this.client
            .apiColumnSettingsByUserIdByTypeGet(this.globalStore.user!.id, ColumnSettingsDto)
            .then((data) => {
                runInAction(() => {
                    this.dataTableStore.setColumns(data.result);
                    const sortedColumn = _.find(this.dataTableStore.columns, (col) => !!col.sort);
                    if (sortedColumn) {
                        this.dataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    this.dataTableStore.loadData();
                });
            })
            .catch(() => {})
            .finally(() => runInAction(() => (this.isLoading = false)));
    }

    @action.bound
    public getAllKpis(pageNumber?: number, pageSize?: number) {
        if (!pageNumber) {
            pageNumber = 1;
        }
        if (!pageSize) {
            pageSize = 25;
        }
        this.isLoading = true;
        return this.client
            .getAllKpis(undefined, pageNumber, pageSize, undefined, undefined)
            .then((resp: SwaggerResponse<IKpiDto[]>) =>
                runInAction(() => {
                    this.kpis = this.setupKpis(resp.result);
                })
            );
    }

    @action.bound
    public openDialogWithKpi(kpi: IKpi) {
        this.isModalOpen = true;
        this.selectedKpi = kpi;
    }

    @action.bound
    public recoverKpi(kpi: IKpi) {
        this.isLoading = true;
        return this.client
            .reactivateKpi(kpi.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    private resetUIState(shouldClearFilter: boolean) {
        if (shouldClearFilter) {
            this.dataTableStore.clearFilter();
        }
        this.dataTableStore.loadData();
        this.closeDialog();
    }

    @action.bound
    public saveKpi(kpi: IKpi) {
        this.isLoading = true;
        let promise;
        const kpiDto = new KpiDto(kpi);
        if (kpi.isNew) {
            kpiDto.isActive = true;
            promise = this.client.createKpi(kpiDto);
        } else {
            promise = this.client.updateKpi(kpi.id, kpiDto);
        }
        return promise
            .then(() => {
                this.resetUIState(kpi.isNew);
            })
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    private setupKpis = (kpis: IKpiDto[]) => {
        return kpis.map((x) => this.setupKpiMenuItems(this.addDefaultFields(x)));
    };

    private addDefaultFields = (kpi: IKpiDto): IKpi => {
        return { ...DefaultKpi(), ...kpi };
    };

    private setupKpiMenuItems = (kpi: IKpi) => {
        kpi.menuItems = [];
        kpi.menuItems.push({
            name: 'Edit',
            onClick: () => this.openDialogWithKpi(kpi),
        });
        if (kpi.isActive) {
            kpi.menuItems.push({
                isConfirm: true,
                name: 'Disable',
                onClick: () => this.disableKpi(kpi),
            });
        } else {
            kpi.menuItems.push({
                isConfirm: true,
                name: 'Recover',
                onClick: () => this.recoverKpi(kpi),
            });
        }
        return kpi;
    };
}



// WEBPACK FOOTER //
// ./src/stores/KpisStore/KpisStore.ts